import type { Channel, Event, StreamChat } from "stream-chat";
import { hive } from "./apiClient";
import type { FriendSummary } from "./social";

export interface DmMember {
	userId: string;
	handle: string;
	displayName: string;
	avatarUrl: string | null;
}

export interface DmConversation {
	id: string;
	peerUserId: string;
	peerHandle: string;
	peerName: string;
	peerAvatarUrl: string | null;
	isGroup: boolean;
	members: DmMember[];
	lastMessage: string;
	updatedAt: string | null;
	unreadCount: number;
}

export interface DmMessage {
	id: string;
	userId: string;
	text: string;
	createdAt: string;
}

/**
 * Stream's own default is 3s, which `listDirectConversations`' queryChannels (30
 * channels, watch + full state) blows through on a slow connection. Applied at
 * construction only — getInstance hands back the existing client, options and all.
 */
const STREAM_TIMEOUT_MS = 20_000;

let client: StreamChat | null = null;
let connectedUserId: string | null = null;
let tokenExpiresAt = 0;

type StreamMessageLike = {
	id?: string;
	text?: string;
	created_at?: string | Date;
	user?: { id?: string } | null;
};

function toIso(value: unknown): string {
	if (typeof value === "string") return value;
	if (value instanceof Date) return value.toISOString();
	return new Date().toISOString();
}

function messageText(message: StreamMessageLike): string {
	return message.text || "";
}

function messageTime(message: StreamMessageLike): string {
	return toIso(message.created_at);
}

export function hiveUserIdFromStream(userId: string): string {
	const separator = userId.lastIndexOf("_");
	return separator >= 0 ? userId.slice(separator + 1) : userId;
}

/** Add the signed-in viewer, which mapConversation intentionally excludes when deriving peers. */
export function conversationMembersWithViewer(
	conversation: Pick<DmConversation, "members">,
	viewer: { id: string; handle: string; displayName?: string | null; avatarUrl?: string | null } | null,
): DmMember[] {
	const all: DmMember[] = viewer
		? [
				{
					userId: viewer.id,
					handle: viewer.handle,
					displayName: viewer.displayName || viewer.handle,
					avatarUrl: viewer.avatarUrl ?? null,
				},
			]
		: [];
	all.push(...conversation.members);
	return [...new Map(all.map((member) => [member.userId, member])).values()];
}

function mapMessage(message: StreamMessageLike): DmMessage {
	return {
		id: message.id ?? `${message.user?.id ?? "message"}-${messageTime(message)}`,
		userId: hiveUserIdFromStream(message.user?.id ?? ""),
		text: messageText(message),
		createdAt: messageTime(message),
	};
}

async function getClient(): Promise<StreamChat> {
	const now = Math.floor(Date.now() / 1000);
	if (client && connectedUserId && tokenExpiresAt - now > 60) return client;

	const [streamModule, credentials] = await Promise.all([
		import("stream-chat"),
		hive.chat.getToken(),
	]);
	if (!client || client.key !== credentials.apiKey) {
		if (client) await client.disconnectUser();
		client = streamModule.StreamChat.getInstance(credentials.apiKey, {
			timeout: STREAM_TIMEOUT_MS,
		});
		connectedUserId = null;
	}
	if (connectedUserId !== credentials.userId) {
		if (connectedUserId) await client.disconnectUser();
		await client.connectUser({ id: credentials.userId }, credentials.token);
		connectedUserId = credentials.userId;
	}
	tokenExpiresAt = credentials.expiresAt;
	return client;
}

/** The app's shared, connected Stream client — reused by the in-playable chat host adapter. */
export function getStreamClient(): Promise<StreamChat> {
	return getClient();
}

/** Connect Stream Chat without opening a specific channel, so app-wide DM events can arrive. */
export async function connectDmClient(): Promise<StreamChat> {
	return getClient();
}

export async function disconnectDmClient(): Promise<void> {
	if (!client) return;
	await client.disconnectUser();
	connectedUserId = null;
	tokenExpiresAt = 0;
}

export async function openDirectChannel(friend: FriendSummary): Promise<Channel> {
	const stream = await getClient();
	const direct = await hive.chat.direct(friend.userId);
	const channel = stream.channel("messaging", direct.channelId);
	await channel.watch();
	return channel;
}

export async function openGroupChannel(friends: FriendSummary[], name?: string): Promise<Channel> {
	const stream = await getClient();
	if (friends.length < 2) throw new Error("group_requires_two_friends");
	const group = await hive.chat.createGroup({
		name: name?.trim() || friends.map((friend) => friend.displayName || friend.handle).join(", "),
		memberIds: friends.map((friend) => friend.userId),
	});
	const channel = stream.channel("messaging", group.channelId);
	await channel.watch();
	return channel;
}

export async function listDirectConversations(friends: FriendSummary[]): Promise<DmConversation[]> {
	const stream = await getClient();
	const friendById = new Map(friends.map((friend) => [friend.userId, friend]));
	const channels = await stream.queryChannels(
		{ type: "messaging", members: { $in: [connectedUserId ?? ""] } },
		{ last_message_at: -1 },
		{ limit: 30, watch: true, state: true },
	);

	return channels
		.map((channel) => mapConversation(channel, friendById))
		.filter((item): item is DmConversation => Boolean(item))
		.filter((item) => item.isGroup || friendById.has(item.peerUserId));
}

export async function loadDirectMessages(channelId: string): Promise<DmMessage[]> {
	const stream = await getClient();
	const channel = stream.channel("messaging", channelId);
	const state = await channel.watch();
	return (state.messages ?? channel.state.messages ?? []).map(mapMessage);
}

export async function markDirectRead(channelId: string): Promise<void> {
	const stream = await getClient();
	const channel = stream.channel("messaging", channelId);
	await channel.markRead().catch(() => null);
}

export async function sendDirectMessage(
	channelId: string,
	text: string,
	mentionedUserIds: string[] = [],
): Promise<DmMessage> {
	const stream = await getClient();
	const channel = stream.channel("messaging", channelId);
	const result = await channel.sendMessage({
		text,
		...(mentionedUserIds.length > 0 ? { mentioned_users: mentionedUserIds } : {}),
	});
	return mapMessage(result.message);
}

export function onStreamEvent(handler: (event: Event) => void): () => void {
	if (!client) return () => {};
	const sub = client.on(handler);
	return () => sub.unsubscribe();
}

function mapConversation(
	channel: Channel,
	friendById: Map<string, FriendSummary>,
): DmConversation | null {
	const members = Object.keys(channel.state.members ?? {});
	const peerIds = members.filter((id) => id !== connectedUserId);
	const peerId = peerIds[0];
	if (!peerId) return null;
	const mappedMembers = peerIds.map((userId) => {
		const hiveUserId = hiveUserIdFromStream(userId);
		const friend = friendById.get(hiveUserId);
		const member = channel.state.members?.[userId] as unknown as {
			user?: { id?: string; name?: string; image?: string; handle?: string };
		};
		const user = member?.user;
		const handle = friend?.handle || user?.handle || userId;
		return {
			userId: hiveUserId,
			handle,
			displayName: friend?.displayName || user?.name || handle,
			avatarUrl: friend?.avatarUrl || user?.image || null,
		};
	});
	const primary = mappedMembers[0]!;
	const isGroup = mappedMembers.length > 1;
	const customData = channel.data as typeof channel.data & { name?: unknown };
	const channelName = typeof customData?.name === "string" ? customData.name : "";
	const last = channel.state.messages[channel.state.messages.length - 1];
	const updatedAt = channel.data?.last_message_at ?? channel.data?.updated_at ?? null;
	return {
		id: channel.id ?? "",
		peerUserId: primary.userId,
		peerHandle: primary.handle,
		peerName: isGroup
			? channelName || mappedMembers.map((member) => member.displayName).join(", ")
			: primary.displayName,
		peerAvatarUrl: isGroup ? null : primary.avatarUrl,
		isGroup,
		members: mappedMembers,
		lastMessage: last ? messageText(last) : "",
		updatedAt: updatedAt ? toIso(updatedAt) : null,
		unreadCount: channel.countUnread(),
	};
}
